<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import { filialSearch } from '@/search-definitions/filialSearch'
import { formatarDataBR } from '@/utils/useDataBR'
import { ENTREPOSTO_ST_REGIME, getNomeStatusRegime, getCorStatusRegime } from '@/constants/ENTREPOSTO_ST_REGIME'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idEntrepostoAduaneiro', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'numeroDocumentoAdmissao', label: 'Nº Documento', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'dataRegistroAdmissao', label: 'Data Registro', align: 'center', sortable: true, slotName: 'dataRegistro' },
  { field: 'nomeLocalEstoque', label: 'Local Estoque', sortable: true, slotName: 'localEstoque' },
  { field: 'embarquesOrigem', label: 'Embarques Origem', sortable: false, slotName: 'embarquesOrigem' },
  { field: 'diasParaVencimento', label: 'Dias', align: 'center', width: 80, sortable: true, slotName: 'dias' },
  { field: 'lstStatusRegime', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field === 'lstStatusRegime') {
    return [...data].sort((a, b) => {
      const cmp = getNomeStatusRegime(a.lstStatusRegime).localeCompare(getNomeStatusRegime(b.lstStatusRegime), 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'diasParaVencimento') {
    return [...data].sort((a, b) => {
      return order === 'asc' ? a.diasParaVencimento - b.diasParaVencimento : b.diasParaVencimento - a.diasParaVencimento
    })
  }
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  busca: '',
  lstStatusRegime: '',
  idFilial: null,
  idLocalEstoque: null,
  termoEmbarqueOrigem: '',
  dataVencimentoDe: '',
  dataVencimentoAte: ''
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaLocalEstoque = computed(() =>
  // Entreposto aduaneiro: restringe explicitamente ao tipo AlfandegadoOuEntreposto.
  // (Antes dependia do default implícito da factory, que agora é [] para não filtrar tipo.)
  criarConfigBuscaLocalEstoque(
    authStore.organizacaoAtiva,
    filtros.idFilial,
    [ESTQ_TIPO_LOCAL_ESTOCAGEM.AlfandegadoOuEntreposto]
  )
)

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('ENTREPOSTO_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('ENTREPOSTO').some(p => p === 'COMPLETO' || p.includes('COMPLETO'))
})

const temPermissaoVisualizacao = computed(() => authStore.pode('ENTREPOSTO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['EntrepostoAduaneiro'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('termo', filtros.busca)
    if (filtros.lstStatusRegime) params.append('lstStatusRegime', filtros.lstStatusRegime)
    if (filtros.idFilial) params.append('idFilial', filtros.idFilial)
    if (filtros.idLocalEstoque) params.append('idLocalEstoque', filtros.idLocalEstoque)
    if (filtros.termoEmbarqueOrigem) params.append('termoEmbarqueOrigem', filtros.termoEmbarqueOrigem)
    if (filtros.dataVencimentoDe) params.append('dataVencimentoDe', filtros.dataVencimentoDe)
    if (filtros.dataVencimentoAte) params.append('dataVencimentoAte', filtros.dataVencimentoAte)

    const response = await api.get(`/EntrepostoAduaneiros?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os regimes de entreposto.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatusRegime = ''
  filtros.idFilial = null
  filtros.idLocalEstoque = null
  filtros.termoEmbarqueOrigem = ''
  filtros.dataVencimentoDe = ''
  filtros.dataVencimentoAte = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/EntrepostoAduaneiros/${item.idEntrepostoAduaneiro}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados do regime.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const regimeParaExcluir = ref(null)
const confirmModal = ref(null)

const solicitarExclusao = (item) => {
  regimeParaExcluir.value = item
  confirmModal.value?.show()
}

const confirmarExclusao = async (confirmado) => {
  if (!confirmado || !regimeParaExcluir.value) {
    regimeParaExcluir.value = null
    return
  }
  const item = regimeParaExcluir.value
  regimeParaExcluir.value = null
  try {
    await api.delete(`/EntrepostoAduaneiros/${item.idEntrepostoAduaneiro}`)
    emit('show-notification', { message: 'Regime excluído com sucesso.', type: 'success' })
    buscarDados(paginacao.value.CurrentPage)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir regime.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idEntrepostoAduaneiro
  auditoriaSelecionada.nome = item.numeroDocumentoAdmissao || `Regime #${item.idEntrepostoAduaneiro}`
  mostrarAuditoria.value = true
}

const obterCorDias = (dias) => {
  if (dias > 60) return 'badge-ghost-success'
  if (dias >= 30) return 'badge-ghost-warning'
  return 'badge-ghost-danger'
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    buscarDados(1)
  }
})

onActivated(async () => {
  await carregarListas()
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Gestão de Entreposto Aduaneiro"
    subtitulo="Declarações de Admissão (DA) — Regime de Entreposto Aduaneiro"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca Geral</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Nº Documento, Local..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatusRegime" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatusRegime || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoBusca
            id="filtroFilial"
            label="Filial"
            v-model="filtros.idFilial"
            :configuracao-busca="configBuscaFilial"
            disabled
          />
        </div>
        <div class="col-md-3">
          <CampoBusca
            id="filtroLocalEstoque"
            label="Local Estoque"
            v-model="filtros.idLocalEstoque"
            :configuracao-busca="configBuscaLocalEstoque"
            @selecionado="aplicarFiltros"
            @show-notification="p => { console.log('[DEBUG EntrepostoAdmin] show-notification recebido do CampoBusca:', JSON.stringify(p)); emit('show-notification', p) }"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Embarque Origem</label>
          <input type="text" class="form-control" v-model="filtros.termoEmbarqueOrigem" placeholder="Nº Processo..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Data Vencimento (de)</label>
          <input type="date" class="form-control" v-model="filtros.dataVencimentoDe" @change="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Data Vencimento (até)</label>
          <input type="date" class="form-control" v-model="filtros.dataVencimentoAte" @change="aplicarFiltros">
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum regime de entreposto encontrado."
      row-key="idEntrepostoAduaneiro"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idEntrepostoAduaneiro }}</span>
      </template>

      <template #dataRegistro="{ row }">
        {{ formatarDataBR(row.dataRegistroAdmissao) }}
      </template>

      <template #localEstoque="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ row.nomeLocalEstoque || row.idLocalEstoque }}
        </span>
      </template>

      <template #embarquesOrigem="{ row }">
        <span v-if="row.itens && row.itens.length > 0" class="text-muted small">
          {{ row.itens.length }} {{ row.itens.length === 1 ? 'embarque' : 'embarques' }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #dias="{ row }">
        <span class="badge" :class="obterCorDias(row.diasParaVencimento)">
          {{ row.diasParaVencimento }} dias
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="getCorStatusRegime(row.lstStatusRegime)">
          {{ getNomeStatusRegime(row.lstStatusRegime) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
          <button v-if="row.lstStatusRegime === ENTREPOSTO_ST_REGIME.Ativo && temPermissaoCompleta" class="btn-icon text-danger" @click="solicitarExclusao(row)" title="Excluir">
            <i class="bi bi-trash-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <ConfirmationModal
    ref="confirmModal"
    title="Excluir Regime de Entreposto"
    message="Deseja realmente excluir este regime de entreposto? Esta ação não pode ser desfeita."
    confirm-button-text="Sim, Excluir"
    cancel-button-text="Não, Manter"
    @confirmed="confirmarExclusao"
  />

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="EntrepostoAduaneiro"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    :tabelas-filhas="['EntrepostoAduaneiroItem']"
    chave-estrangeira="IdEntrepostoAduaneiro"
    @fechar="mostrarAuditoria = false"
  />
</template>
